import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Github,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  ExternalLink,
  Search,
  ArrowLeft,
  FileText,
  FilePlus,
  FileX,
  FilePen,
  Clock,
  Download,
  GitBranch,
  Plus,
  Radio,
  RotateCcw,
  AlertCircle,
  AlertTriangle,
  Upload,
  Image,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn, generateVividGradient } from '@/lib/utils';
import { useGitStatus } from '@/hooks/useGitStatus';
import { useGitSettings } from '@/hooks/useGitSettings';
import { useGit } from '@/hooks/useGit';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAppContext } from '@/hooks/useAppContext';
import { useGitHubOAuth } from '@/hooks/useGitHubOAuth';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useProjectDeploySettings } from '@/hooks/useProjectDeploySettings';
import { findCredentialsForRepo } from '@/lib/gitCredentials';
import { nip19 } from 'nostr-tools';

type ConnectionState = 'disconnected' | 'connecting' | 'selecting-repo' | 'connected';
type SyncProvider = 'github' | 'nostr' | null;
type AdvancedView = 'main' | 'changes' | 'history' | 'pull' | 'branches';

interface SaveCodeDialogProps {
  projectId: string;
  projectName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface GitHubRepo {
  name: string;
  full_name: string;
  updated_at: string;
  html_url: string;
  clone_url: string;
}

interface CommitInfo {
  oid: string;
  message: string;
  time: string;
}

export function SaveCodeDialog({ projectId, projectName, open, onOpenChange }: SaveCodeDialogProps) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [repoSearch, setRepoSearch] = useState('');
  const [provider, setProvider] = useState<SyncProvider>(null);

  const [advancedView, setAdvancedView] = useState<AdvancedView>('main');
  const [isPulling, setIsPulling] = useState(false);
  const [rollbackConfirm, setRollbackConfirm] = useState<string | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [skipRemoteChanges, setSkipRemoteChanges] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);

  // Cover image state
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // GitHub-specific state
  const [gitHubRepos, setGitHubRepos] = useState<GitHubRepo[]>([]);
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [branches, setBranches] = useState<string[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isSwitchingBranch, setIsSwitchingBranch] = useState(false);
  const [commitHistory, setCommitHistory] = useState<CommitInfo[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const navigate = useNavigate();
  const { data: gitStatus, refetch: refetchGitStatus } = useGitStatus(projectId);
  const { settings } = useGitSettings();
  const { git } = useGit();
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { config } = useAppContext();
  const { startOAuth } = useGitHubOAuth();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { settings: projectSettings, saveSettings: saveProjectSettings } = useProjectDeploySettings(projectId);

  const projectPath = `/projects/${projectId}`;
  const gradient = generateVividGradient(projectId);

  // Load cover image from project settings
  useEffect(() => {
    if (projectSettings.coverImageUrl) {
      setCoverImageUrl(projectSettings.coverImageUrl);
    }
  }, [projectSettings.coverImageUrl]);

  // Calculate pending changes and remote changes
  const pendingChanges = gitStatus?.changedFiles.length || 0;
  const remoteChanges = gitStatus?.behind || 0;
  const hasRemoteChanges = remoteChanges > 0 && provider === 'github';

  // Determine connection state from git status
  useEffect(() => {
    if (!gitStatus?.isGitRepo) {
      setConnectionState('disconnected');
      return;
    }

    const originRemote = gitStatus.remotes.find(r => r.name === 'origin');
    if (!originRemote) {
      setConnectionState('disconnected');
      return;
    }

    // Determine provider from origin URL
    if (originRemote.url.startsWith('nostr://')) {
      setProvider('nostr');
      setConnectionState('connected');
    } else if (originRemote.url.includes('github.com')) {
      setProvider('github');
      setConnectionState('connected');
      // Extract repo name from URL
      const match = originRemote.url.match(/github\.com[:/](.+?)(?:\.git)?$/);
      if (match) {
        setSelectedRepo(match[1]);
      }
    } else {
      setConnectionState('connected');
      setProvider('github'); // Default to github for other URLs
    }
  }, [gitStatus]);

  // Check if GitHub is connected (has credentials)
  const hasGitHubCredentials = settings.credentials?.['https://github.com'] !== undefined;
  const gitHubHostToken = settings.hostTokens?.['github.com'];

  // Load branches when connected
  const loadBranches = useCallback(async () => {
    if (!gitStatus?.isGitRepo) return;
    setIsLoadingBranches(true);
    try {
      const localBranches = await git.listBranches({ dir: projectPath });
      setBranches(localBranches);
    } catch (error) {
      console.error('Failed to load branches:', error);
    } finally {
      setIsLoadingBranches(false);
    }
  }, [git, projectPath, gitStatus?.isGitRepo]);

  // Load commit history
  const loadCommitHistory = useCallback(async () => {
    if (!gitStatus?.isGitRepo) return;
    setIsLoadingHistory(true);
    try {
      const commits = await git.log({ dir: projectPath, depth: 10 });
      setCommitHistory(commits.map(c => ({
        oid: c.oid,
        message: c.commit.message.split('\n')[0],
        time: formatRelativeTime(c.commit.author.timestamp),
      })));
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [git, projectPath, gitStatus?.isGitRepo]);

  useEffect(() => {
    if (open && gitStatus?.isGitRepo) {
      loadBranches();
    }
  }, [open, gitStatus?.isGitRepo, loadBranches]);

  useEffect(() => {
    if (advancedView === 'history') {
      loadCommitHistory();
    }
  }, [advancedView, loadCommitHistory]);

  // Load GitHub repos when connecting
  const loadGitHubRepos = useCallback(async () => {
    if (!gitHubHostToken?.token) return;
    setIsLoadingRepos(true);
    try {
      // Fetch user repos via GitHub API
      const apiUrl = 'https://api.github.com/user/repos?sort=updated&per_page=50';
      const response = await fetch(
        config.corsProxy
          ? `${config.corsProxy}?url=${encodeURIComponent(apiUrl)}`
          : apiUrl,
        {
          headers: {
            'Authorization': `Bearer ${gitHubHostToken.token}`,
            'Accept': 'application/vnd.github.v3+json',
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch repos');
      const repos: GitHubRepo[] = await response.json();
      setGitHubRepos(repos);
    } catch (error) {
      console.error('Failed to load GitHub repos:', error);
      toast({
        title: 'Failed to load repositories',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRepos(false);
    }
  }, [gitHubHostToken, config.corsProxy, toast]);

  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now();
    const commitTime = timestamp * 1000;
    const diffMs = now - commitTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatLastSynced = () => {
    if (!gitStatus?.latestCommit) return 'Never';
    return formatRelativeTime(gitStatus.latestCommit.author.timestamp);
  };

  // Cover image upload handlers
  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setIsUploadingImage(true);

    try {
      // Upload to Blossom
      const [[_, url]] = await uploadFile(file);

      setCoverImageUrl(url);
      // Save to project settings
      await saveProjectSettings({
        ...projectSettings,
        coverImageUrl: url,
      });

      toast({
        title: 'Image uploaded!',
        description: 'Your cover image has been set',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveCoverImage = async () => {
    setCoverImageUrl(null);
    await saveProjectSettings({
      ...projectSettings,
      coverImageUrl: undefined,
    });
    toast({
      title: 'Cover image removed',
    });
  };

  const handleConnect = async () => {
    if (!hasGitHubCredentials && !gitHubHostToken) {
      // Start OAuth flow
      startOAuth();
      return;
    }
    setConnectionState('connecting');
    await loadGitHubRepos();
    setConnectionState('selecting-repo');
  };

  const handleSelectRepo = async (repo: GitHubRepo) => {
    setSelectedRepo(repo.full_name);
    setConnectionState('connecting');

    try {
      // Set origin remote
      try {
        await git.deleteRemote({ dir: projectPath, remote: 'origin' });
      } catch {
        // Ignore if origin doesn't exist
      }

      await git.addRemote({
        dir: projectPath,
        remote: 'origin',
        url: repo.clone_url,
      });

      toast({
        title: 'Repository connected',
        description: `Connected to ${repo.full_name}`,
      });

      await refetchGitStatus();
      setConnectionState('connected');
    } catch (error) {
      toast({
        title: 'Failed to connect repository',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      setConnectionState('selecting-repo');
    }
  };

  const handleSync = async () => {
    if (skipRemoteChanges && remoteChanges > 0 && !showOverrideConfirm) {
      setShowOverrideConfirm(true);
      return;
    }

    if (!gitStatus?.isGitRepo || !gitStatus.currentBranch) {
      toast({
        title: 'Cannot sync',
        description: 'Not a git repository or no current branch',
        variant: 'destructive',
      });
      return;
    }

    if (gitStatus.ahead === 0 && pendingChanges === 0) {
      toast({
        title: 'Nothing to push',
        description: 'No changes to sync',
      });
      return;
    }

    setIsSyncing(true);
    setSyncStatus('idle');

    try {
      // First commit any uncommitted changes
      if (pendingChanges > 0) {
        // Stage all changes
        const statusMatrix = await git.statusMatrix({ dir: projectPath });
        for (const [filepath, headStatus, workdirStatus, stageStatus] of statusMatrix) {
          if (headStatus !== workdirStatus || workdirStatus !== stageStatus) {
            if (workdirStatus === 0) {
              await git.remove({ dir: projectPath, filepath });
            } else {
              await git.add({ dir: projectPath, filepath });
            }
          }
        }

        // Commit
        await git.commit({
          dir: projectPath,
          message: `Update from Shakespeare`,
        });
      }

      // Push to remote
      const remote = gitStatus.remotes[0];
      await git.push({
        dir: projectPath,
        remote: remote.name,
        ref: gitStatus.currentBranch,
        onAuth: (url) => findCredentialsForRepo(url, settings.credentials),
        signer: user?.signer,
      });

      toast({
        title: 'Sync successful',
        description: `Changes pushed to ${remote.name}/${gitStatus.currentBranch}`,
      });

      setSyncStatus('success');
      setSkipRemoteChanges(false);
      setShowOverrideConfirm(false);
      await refetchGitStatus();

      setTimeout(() => setSyncStatus('idle'), 3000);
    } catch (error) {
      setSyncStatus('error');
      toast({
        title: 'Sync failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleNostrSave = async () => {
    if (!user) {
      toast({
        title: 'Cannot save to Nostr',
        description: 'You must be logged in to save to Nostr',
        variant: 'destructive',
      });
      return;
    }

    setProvider('nostr');
    setIsSyncing(true);

    try {
      // Create repo announcement event (NIP-34 kind 30617)
      const cloneUrls = config.ngitServers.map(server =>
        `https://${server}/${nip19.npubEncode(user.pubkey)}/${projectId}.git`
      );
      const relayUrls = config.ngitServers.map(server => `wss://${server}`);

      await publishEvent({
        kind: 30617,
        content: '',
        tags: [
          ['d', projectId],
          ['clone', ...cloneUrls],
          ['relays', ...relayUrls],
        ],
      });

      // Get repository refs
      const refTags: string[][] = [];
      const refs = await git.listRefs({ dir: projectPath });

      for (const ref of refs) {
        try {
          const commitId = await git.resolveRef({ dir: projectPath, ref });
          if (ref.startsWith('refs/heads/') || ref.startsWith('refs/tags/')) {
            refTags.push([ref, commitId]);
          }
        } catch {
          // Skip refs that can't be resolved
        }
      }

      // Get HEAD reference
      let headRef: string | undefined;
      const currentBranch = await git.currentBranch({ dir: projectPath });
      if (currentBranch) {
        headRef = `ref: refs/heads/${currentBranch}`;
      }

      // Create repo state event (NIP-34 kind 30618)
      await publishEvent({
        kind: 30618,
        content: '',
        tags: [
          ['d', projectId],
          ...refTags,
          ...(headRef ? [['HEAD', headRef]] : []),
        ],
      });

      // Set origin to Nostr URL
      const nostrUrl = `nostr://${nip19.npubEncode(user.pubkey)}/${projectId}`;

      try {
        await git.deleteRemote({ dir: projectPath, remote: 'origin' });
      } catch {
        // Ignore
      }

      await git.addRemote({
        dir: projectPath,
        remote: 'origin',
        url: nostrUrl,
      });

      toast({
        title: 'Saved to Nostr',
        description: 'Your code has been published to Nostr',
      });

      setSyncStatus('success');
      setConnectionState('connected');
      await refetchGitStatus();

      setTimeout(() => setSyncStatus('idle'), 2000);
    } catch (error) {
      toast({
        title: 'Failed to save to Nostr',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePull = async () => {
    if (!gitStatus?.isGitRepo || !gitStatus.currentBranch) return;

    setIsPulling(true);
    try {
      const remote = gitStatus.remotes[0];

      await git.pull({
        dir: projectPath,
        ref: gitStatus.currentBranch,
        singleBranch: true,
        author: {
          name: 'shakespeare.diy',
          email: 'assistant@shakespeare.diy',
        },
        onAuth: (url) => findCredentialsForRepo(url, settings.credentials),
      });

      toast({
        title: 'Pull successful',
        description: `Latest changes pulled from ${remote.name}/${gitStatus.currentBranch}`,
      });

      await refetchGitStatus();
      setAdvancedView('main');
      onOpenChange(false);
      navigate(`/project/${projectId}?build`);
    } catch (error) {
      toast({
        title: 'Pull failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsPulling(false);
    }
  };

  const handleSwitchBranch = async (branchName: string) => {
    setIsSwitchingBranch(true);
    try {
      await git.checkout({ dir: projectPath, ref: branchName });
      toast({
        title: 'Branch switched',
        description: `Switched to branch "${branchName}"`,
      });
      await refetchGitStatus();
      onOpenChange(false);
      navigate(`/project/${projectId}?build`);
    } catch (error) {
      toast({
        title: 'Failed to switch branch',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSwitchingBranch(false);
    }
  };

  const handleRollback = async (commitOid: string) => {
    setIsRollingBack(true);
    try {
      toast({
        title: 'Rollback initiated',
        description: 'Please use the full Rollback dialog for complete rollback functionality',
      });

      setRollbackConfirm(null);
      setAdvancedView('main');
    } catch (error) {
      toast({
        title: 'Rollback failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsRollingBack(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await git.deleteRemote({ dir: projectPath, remote: 'origin' });
    } catch {
      // Ignore
    }

    setConnectionState('disconnected');
    setSelectedRepo(null);
    setShowAdvanced(false);
    setProvider(null);
    setAdvancedView('main');
    await refetchGitStatus();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'modified':
        return <FilePen className="h-3.5 w-3.5 text-amber-400" />;
      case 'added':
        return <FilePlus className="h-3.5 w-3.5 text-emerald-400" />;
      case 'deleted':
        return <FileX className="h-3.5 w-3.5 text-red-400" />;
      default:
        return <FileText className="h-3.5 w-3.5 text-zinc-400" />;
    }
  };

  const filteredRepos = gitHubRepos.filter(repo =>
    repo.name.toLowerCase().includes(repoSearch.toLowerCase()) ||
    repo.full_name.toLowerCase().includes(repoSearch.toLowerCase())
  );

  const isGitHub = provider === 'github';
  const canSave = (pendingChanges > 0 || (gitStatus?.ahead || 0) > 0) && (!hasRemoteChanges || skipRemoteChanges) && !showOverrideConfirm;

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setShowOverrideConfirm(false);
      setAdvancedView('main');
    }
  }, [open]);

  const renderContent = () => {
    // Disconnected state - show provider selection
    if (connectionState === 'disconnected' && provider === null) {
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            {/* GitHub Option */}
            <button
              onClick={() => {
                setProvider('github');
                if (!hasGitHubCredentials && !gitHubHostToken) {
                  startOAuth();
                } else {
                  handleConnect();
                }
              }}
              className="w-full p-4 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">
                  <Github className="h-5 w-5 text-zinc-900 dark:text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-zinc-900 dark:text-zinc-100">GitHub</div>
                  <div className="text-sm text-zinc-500">Version control</div>
                </div>
              </div>
            </button>

            {/* Nostr Option */}
            <button
              onClick={handleNostrSave}
              disabled={isSyncing || !user}
              className="w-full p-4 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-purple-500/50 hover:bg-purple-50 dark:hover:bg-purple-500/5 transition-all text-left group disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-purple-100 dark:bg-purple-500/10 group-hover:bg-purple-200 dark:group-hover:bg-purple-500/20">
                  {isSyncing && provider === 'nostr' ? (
                    <Loader2 className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-spin" />
                  ) : (
                    <Radio className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-zinc-900 dark:text-zinc-100">
                    {isSyncing && provider === 'nostr' ? 'Saving...' : 'Save to Nostr'}
                  </div>
                  <div className="text-sm text-zinc-500">
                    {user ? 'Decentralized backup' : 'Log in to use Nostr'}
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      );
    }

    // GitHub disconnected - show connect button
    if (connectionState === 'disconnected' && provider === 'github') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setProvider(null)}
              className="p-1 -ml-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="p-2.5 rounded-xl bg-zinc-100 dark:bg-zinc-800">
              <Github className="h-6 w-6 text-zinc-900 dark:text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Connect to GitHub</h3>
              <p className="text-xs text-zinc-500">Sync your project automatically</p>
            </div>
          </div>

          <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500" />
              <span>One-click save to repository</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500" />
              <span>See pending changes at a glance</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500" />
              <span>Full version history</span>
            </div>
          </div>

          <Button
            onClick={handleConnect}
            className="w-full gap-2"
          >
            <Github className="h-4 w-4" />
            Connect GitHub Account
          </Button>

          <p className="text-xs text-zinc-500 text-center">You'll be redirected to authorize access</p>
        </div>
      );
    }

    // Connecting state
    if (connectionState === 'connecting') {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
          <span className="ml-2 text-zinc-500">Connecting...</span>
        </div>
      );
    }

    // Repository selection
    if (connectionState === 'selecting-repo' && provider === 'github') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-emerald-500" />
            <span className="text-zinc-600 dark:text-zinc-300">Connected as</span>
            <span className="text-zinc-900 dark:text-zinc-100 font-medium">@{gitHubHostToken?.username || 'user'}</span>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search repositories..."
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              className="w-full pl-9"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border rounded-lg">
            {isLoadingRepos ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
              </div>
            ) : filteredRepos.length === 0 ? (
              <div className="py-8 text-center text-zinc-500 text-sm">
                No repositories found
              </div>
            ) : (
              filteredRepos.map((repo) => (
                <button
                  key={repo.full_name}
                  onClick={() => handleSelectRepo(repo)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-b last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <Github className="h-4 w-4 text-zinc-900 dark:text-white" />
                    <span className="text-sm text-zinc-700 dark:text-zinc-200">{repo.name}</span>
                  </div>
                  <span className="text-xs text-zinc-500">
                    {new Date(repo.updated_at).toLocaleDateString()}
                  </span>
                </button>
              ))
            )}
          </div>

          <button
            onClick={() => window.open('https://github.com/new', '_blank')}
            className="w-full flex items-center justify-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors py-2 border border-dashed rounded-lg hover:border-zinc-400"
          >
            <span>Create new repository</span>
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      );
    }

    // Connected state
    const ProviderIcon = isGitHub ? Github : Radio;

    // View Changes
    if (advancedView === 'changes') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAdvancedView('main')}
              className="p-1 -ml-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <FileText className="h-4 w-4 text-zinc-500" />
            <h3 className="font-medium text-zinc-900 dark:text-zinc-100">Pending Changes</h3>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {gitStatus?.changedFiles.map((file) => (
              <div key={file.filepath} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800">
                {getStatusIcon(file.status)}
                <span className="text-sm text-zinc-700 dark:text-zinc-300 truncate flex-1">{file.filepath}</span>
                <span className="text-xs text-zinc-500 capitalize">{file.status}</span>
              </div>
            ))}
            {(!gitStatus?.changedFiles || gitStatus.changedFiles.length === 0) && (
              <div className="py-4 text-center text-zinc-500 text-sm">
                No pending changes
              </div>
            )}
          </div>

          {gitStatus?.changedFiles && gitStatus.changedFiles.length > 0 && (
            <div className="pt-2 border-t">
              <div className="text-xs text-zinc-500">
                {gitStatus.changedFiles.length} files changed
              </div>
            </div>
          )}
        </div>
      );
    }

    // Commit History
    if (advancedView === 'history') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAdvancedView('main')}
              className="p-1 -ml-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Clock className="h-4 w-4 text-zinc-500" />
            <h3 className="font-medium text-zinc-900 dark:text-zinc-100">History</h3>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {isLoadingHistory ? (
              <div className="py-4 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
              </div>
            ) : commitHistory.length === 0 ? (
              <div className="py-4 text-center text-zinc-500 text-sm">
                No commit history
              </div>
            ) : (
              commitHistory.map((commit, i) => (
                <div key={commit.oid} className="group">
                  {rollbackConfirm === commit.oid ? (
                    <div className="px-2 py-3 rounded bg-zinc-100 dark:bg-zinc-800 border space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center mt-1">
                          <div className={cn('w-2 h-2 rounded-full', isGitHub ? 'bg-amber-500' : 'bg-purple-500')} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-700 dark:text-zinc-200">Rollback to this version?</p>
                          <p className="text-xs text-zinc-500 mt-0.5 truncate">{commit.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-5">
                        <button
                          onClick={() => handleRollback(commit.oid)}
                          disabled={isRollingBack}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                        >
                          {isRollingBack ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Rolling back...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="h-3 w-3" />
                              Confirm Rollback
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setRollbackConfirm(null)}
                          disabled={isRollingBack}
                          className="px-3 py-1.5 text-xs font-medium rounded bg-zinc-200 dark:bg-zinc-700 hover:bg-zinc-300 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-300 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3 px-2 py-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800">
                      <div className="flex flex-col items-center mt-1">
                        <div className={cn('w-2 h-2 rounded-full', isGitHub ? 'bg-amber-500' : 'bg-purple-500')} />
                        {i < commitHistory.length - 1 && <div className="w-px h-8 bg-zinc-200 dark:bg-zinc-800 mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-zinc-700 dark:text-zinc-200 truncate">{commit.message}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-zinc-500">{commit.time}</span>
                          <code className="text-xs text-zinc-400">{commit.oid.substring(0, 7)}</code>
                        </div>
                      </div>
                      {i > 0 && (
                        <button
                          onClick={() => setRollbackConfirm(commit.oid)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-opacity"
                          title="Rollback to this version"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      );
    }

    // Pull Latest
    if (advancedView === 'pull' && isGitHub) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAdvancedView('main')}
              className="p-1 -ml-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Download className="h-4 w-4 text-zinc-500" />
            <h3 className="font-medium text-zinc-900 dark:text-zinc-100">Pull Latest</h3>
          </div>

          <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Remote changes</span>
              <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                {remoteChanges} ahead
              </span>
            </div>
          </div>

          <Button
            onClick={handlePull}
            disabled={isPulling}
            className="w-full gap-2"
          >
            {isPulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isPulling ? 'Pulling...' : 'Pull Changes'}
          </Button>
        </div>
      );
    }

    // Branch Settings
    if (advancedView === 'branches' && isGitHub) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAdvancedView('main')}
              className="p-1 -ml-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <GitBranch className="h-4 w-4 text-zinc-500" />
            <h3 className="font-medium text-zinc-900 dark:text-zinc-100">Branches</h3>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {isLoadingBranches ? (
              <div className="py-4 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
              </div>
            ) : (
              branches.map((branch) => (
                <button
                  key={branch}
                  onClick={() => handleSwitchBranch(branch)}
                  disabled={isSwitchingBranch}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors',
                    gitStatus?.currentBranch === branch
                      ? 'bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30'
                      : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <GitBranch
                      className={cn(
                        'h-4 w-4',
                        gitStatus?.currentBranch === branch ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-500'
                      )}
                    />
                    <span
                      className={cn(
                        'text-sm',
                        gitStatus?.currentBranch === branch ? 'text-amber-700 dark:text-amber-100' : 'text-zinc-700 dark:text-zinc-300'
                      )}
                    >
                      {branch}
                    </span>
                  </div>
                  {gitStatus?.currentBranch === branch && <Check className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                </button>
              ))
            )}
          </div>

          <button className="w-full flex items-center justify-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors py-2 border border-dashed rounded-lg hover:border-zinc-400">
            <Plus className="h-4 w-4" />
            <span>Create new branch</span>
          </button>
        </div>
      );
    }

    // Main View
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ProviderIcon className={cn('h-4 w-4', isGitHub ? 'text-zinc-900 dark:text-white' : 'text-purple-600 dark:text-purple-400')} />
            <span className="font-medium text-zinc-900 dark:text-zinc-100">{isGitHub ? 'GitHub' : 'Nostr'}</span>
          </div>
          {syncStatus === 'success' && <Check className="h-4 w-4 text-emerald-500" />}
        </div>
        {gitStatus?.latestCommit && (
          <p className="text-xs text-zinc-500">Last saved {formatLastSynced()}</p>
        )}

        {/* Cover Image Section */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
          {/* Cover image preview */}
          <div className="relative w-16 h-10 rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 flex-shrink-0">
            {coverImageUrl ? (
              <img
                src={coverImageUrl}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ background: gradient }}
              >
                <Image className="h-4 w-4 text-white/50" />
              </div>
            )}
          </div>

          {/* Info and upload button */}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-zinc-600 dark:text-zinc-400 truncate">
              {coverImageUrl ? 'Cover image set' : 'Add a cover image'}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {coverImageUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveCoverImage}
                className="h-7 w-7 p-0 text-zinc-500 hover:text-red-500"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleImageUploadClick}
              disabled={isUploadingImage}
              className="h-7 px-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200"
            >
              {isUploadingImage ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileChange}
            className="hidden"
          />
        </div>

        {/* Status */}
        <div className="space-y-3">
          {showOverrideConfirm && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">Override remote changes?</p>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                    You have {remoteChanges} update{remoteChanges > 1 ? 's' : ''} on GitHub that will be lost.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-6">
                <Button
                  onClick={handleSync}
                  size="sm"
                  variant="destructive"
                  className="text-xs"
                >
                  Yes, Override
                </Button>
                <Button
                  onClick={() => {
                    setShowOverrideConfirm(false);
                    setSkipRemoteChanges(false);
                  }}
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {hasRemoteChanges && !showOverrideConfirm && (
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-200">
                    Your code is behind by {remoteChanges} update{remoteChanges > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">Get the latest to avoid conflicts</p>
                </div>
              </div>
              <Button
                onClick={handlePull}
                disabled={isPulling}
                size="sm"
                className="w-full gap-2"
              >
                {isPulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {isPulling ? 'Getting latest...' : 'Get Latest'}
              </Button>

              <label className="flex items-center gap-2 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={skipRemoteChanges}
                    onChange={(e) => setSkipRemoteChanges(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="w-4 h-4 border border-zinc-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-800 peer-checked:bg-amber-500 peer-checked:border-amber-500 transition-colors" />
                  <Check className="absolute inset-0 h-4 w-4 text-white opacity-0 peer-checked:opacity-100 p-0.5" />
                </div>
                <span className="text-xs text-zinc-500 group-hover:text-zinc-600 dark:group-hover:text-zinc-400">
                  Skip and override remote changes
                </span>
              </label>
            </div>
          )}

          {!hasRemoteChanges && (pendingChanges > 0 || (gitStatus?.ahead || 0) > 0) && !showOverrideConfirm && (
            <div
              className={cn(
                'flex items-center justify-between p-3 rounded-lg',
                isGitHub ? 'bg-amber-50 dark:bg-amber-500/10' : 'bg-purple-50 dark:bg-purple-500/10'
              )}
            >
              <div className="flex items-center gap-2">
                <div className={cn('w-2 h-2 rounded-full', isGitHub ? 'bg-amber-500' : 'bg-purple-500')} />
                <span className="text-sm text-zinc-700 dark:text-zinc-200">
                  {pendingChanges + (gitStatus?.ahead || 0)} unsaved change{pendingChanges + (gitStatus?.ahead || 0) !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}

          {remoteChanges === 0 && pendingChanges === 0 && (gitStatus?.ahead || 0) === 0 && !showOverrideConfirm && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10">
              <Check className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-zinc-700 dark:text-zinc-200">All changes saved</span>
            </div>
          )}

          {!showOverrideConfirm && (
            <Button
              onClick={handleSync}
              disabled={isSyncing || !canSave}
              className={cn(
                'w-full gap-2 font-medium',
                isGitHub
                  ? 'bg-amber-500 hover:bg-amber-400 text-zinc-900'
                  : 'bg-purple-500 hover:bg-purple-400 text-white',
                (isSyncing || !canSave) && 'opacity-50'
              )}
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ProviderIcon className="h-4 w-4" />
              )}
              {isSyncing ? 'Saving...' : isGitHub ? 'Save to GitHub' : 'Save to Nostr'}
            </Button>
          )}

          {hasRemoteChanges && (pendingChanges > 0 || (gitStatus?.ahead || 0) > 0) && !skipRemoteChanges && !showOverrideConfirm && (
            <p className="text-xs text-zinc-500 text-center">
              Get the latest updates first to save your {pendingChanges + (gitStatus?.ahead || 0)} changes
            </p>
          )}
        </div>

        {/* Advanced */}
        <div className="border-t pt-4">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            <span>Advanced</span>
            {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showAdvanced && (
            <div className="mt-3 space-y-1">
              <button
                onClick={() => setAdvancedView('changes')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>View changes</span>
                {pendingChanges > 0 && (
                  <span className="ml-auto text-xs text-zinc-500">{pendingChanges}</span>
                )}
              </button>
              <button
                onClick={() => setAdvancedView('history')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span>History</span>
              </button>

              {/* GitHub-only options */}
              {isGitHub && (
                <>
                  <button
                    onClick={() => setAdvancedView('pull')}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    <span>Pull latest</span>
                    {remoteChanges > 0 && (
                      <span className="ml-auto text-xs text-emerald-600 dark:text-emerald-400">{remoteChanges} new</span>
                    )}
                  </button>
                  <button
                    onClick={() => setAdvancedView('branches')}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                  >
                    <GitBranch className="h-4 w-4" />
                    <span>Branch settings</span>
                    <span className="ml-auto text-xs text-zinc-500">{gitStatus?.currentBranch}</span>
                  </button>
                  {selectedRepo && (
                    <a
                      href={`https://github.com/${selectedRepo}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      <span>View project</span>
                    </a>
                  )}
                </>
              )}

              <div className="pt-2 mt-2 border-t">
                <button
                  onClick={handleDisconnect}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <span>Disconnect</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Your Code</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
